import React from 'react';
import { observer } from 'mobx-react-lite';
import { FaFont, FaImage, FaVectorSquare, FaTrash, FaArrowUp, FaArrowDown, FaShapes } from 'react-icons/fa';
import editorStore, { ElementProxy } from '../../stores/EditorStore';

const C = {
  surface:    '#1c1e2e',
  surface2:   '#252840',
  border:     'rgba(255,255,255,0.07)',
  text1:      '#f0f2ff',
  text2:      '#9da3c8',
  text3:      '#5c6290',
  accent:     '#5b79ff',
  accentSoft: 'rgba(91,121,255,0.15)',
  accentBorder: 'rgba(91,121,255,0.25)',
};

function typeIcon(el: ElementProxy) {
  const t = el.type || el.extensionType || '';
  if (t === 'text' || t === 'textbox' || t === 'i-text')
    return <FaFont size={10} style={{ color: '#7c9fff' }} />;
  if (t === 'image')
    return <FaImage size={10} style={{ color: '#4ade80' }} />;
  if (t === 'svg')
    return <FaVectorSquare size={10} style={{ color: '#a78bfa' }} />;
  return <FaShapes size={10} style={{ color: C.text3 }} />;
}

function typeLabel(el: ElementProxy): string {
  const t = el.type || '';
  if (t === 'text' || t === 'textbox' || t === 'i-text')
    return el.text ? el.text.slice(0, 22) : 'Text';
  if (t === 'image') return 'Image';
  if (t === 'svg') return el.extensionType === 'wrapOverlay' ? 'Wrap overlay' : 'SVG';
  return t || 'Object';
}

const LayerPanel: React.FC = observer(() => {
  const layers = editorStore.layers;
  const selectedIds = new Set(editorStore.selectedElements.map(e => e.id));

  if (layers.length === 0) {
    return (
      <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        height: '100%', gap: 10, padding: 24,
      }}>
        <FaShapes size={24} style={{ color: C.text3, opacity: 0.4 }} />
        <span style={{ fontSize: 12, color: C.text3, textAlign: 'center' }}>
          No objects on canvas yet.
        </span>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <div style={{
        padding: '8px 12px',
        borderBottom: `1px solid ${C.border}`,
        fontSize: 10, fontWeight: 700, letterSpacing: 0.8,
        textTransform: 'uppercase' as const, color: C.text3,
      }}>
        Layers ({layers.length})
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '4px 6px' }}>
        {layers.map((el, idx) => {
          const isSelected = selectedIds.has(el.id);
          const isLocked   = !el.selectable;
          return (
            <div
              key={el.id || idx}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '6px 8px', borderRadius: 7, marginBottom: 2,
                cursor: 'pointer',
                background: isSelected ? C.accentSoft : 'none',
                border: `1px solid ${isSelected ? C.accentBorder : 'transparent'}`,
                transition: 'all 150ms',
              }}
              onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; }}
              onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = 'none'; }}
              onClick={() => el.id && editorStore.selectObjectById(el.id)}
            >
              {/* Type icon badge */}
              <div style={{
                width: 26, height: 26, borderRadius: 6, flexShrink: 0,
                background: C.surface2,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                {typeIcon(el)}
              </div>

              {/* Label */}
              <span style={{
                flex: 1, fontSize: 12,
                color: isLocked ? C.text3 : C.text2,
                fontStyle: isLocked ? 'italic' : 'normal',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {typeLabel(el)}
                {isLocked && <span style={{ marginLeft: 4, fontSize: 10, color: C.text3 }}>(locked)</span>}
              </span>

              {/* Actions */}
              {!isLocked && (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 1,
                  opacity: isSelected ? 1 : 0, transition: 'opacity 150ms',
                }}
                  className="layer-actions"
                >
                  <button
                    style={{ padding: 3, background: 'none', border: 'none', color: C.text3, cursor: 'pointer', borderRadius: 4, display: 'flex' }}
                    onMouseEnter={e => { e.currentTarget.style.color = C.accent; e.currentTarget.style.background = C.accentSoft; }}
                    onMouseLeave={e => { e.currentTarget.style.color = C.text3; e.currentTarget.style.background = 'none'; }}
                    onClick={ev => { ev.stopPropagation(); editorStore.selectObjectById(el.id); editorStore.bringForward(); }}
                    title="Bring forward"
                  ><FaArrowUp size={9} /></button>
                  <button
                    style={{ padding: 3, background: 'none', border: 'none', color: C.text3, cursor: 'pointer', borderRadius: 4, display: 'flex' }}
                    onMouseEnter={e => { e.currentTarget.style.color = C.accent; e.currentTarget.style.background = C.accentSoft; }}
                    onMouseLeave={e => { e.currentTarget.style.color = C.text3; e.currentTarget.style.background = 'none'; }}
                    onClick={ev => { ev.stopPropagation(); editorStore.selectObjectById(el.id); editorStore.sendBackward(); }}
                    title="Send backward"
                  ><FaArrowDown size={9} /></button>
                  <button
                    style={{ padding: 3, background: 'none', border: 'none', color: C.text3, cursor: 'pointer', borderRadius: 4, display: 'flex' }}
                    onMouseEnter={e => { e.currentTarget.style.color = '#ff5b6b'; e.currentTarget.style.background = 'rgba(255,91,107,0.12)'; }}
                    onMouseLeave={e => { e.currentTarget.style.color = C.text3; e.currentTarget.style.background = 'none'; }}
                    onClick={ev => { ev.stopPropagation(); editorStore.selectObjectById(el.id); editorStore.deleteSelected(); }}
                    title="Delete"
                  ><FaTrash size={9} /></button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <style>{`
        div:hover .layer-actions { opacity: 1 !important; }
      `}</style>
    </div>
  );
});

export default LayerPanel;
